import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';

import { StatusCode } from '../../api/enumerations';
import {
  createMaintenance,
  deleteMaintenance,
  getMaintenance,
} from '../../api/maintenance';
import { CreateMaintenanceData } from '../../api/maintenance/types';
import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import {
  TitleTypography,
  WhiteCircularProgress,
} from '../../components/UI/Typography';
import { Constants } from '../../constants/configuration';
import { IconSettingsMS } from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { formatTime } from '../../helpers';
import useErrorMessage from '../../hooks/useErrorMessage';
import { ResetPassword } from './ResetPasswordDialog';
import {
  OutlinedButton,
  datePickerStyle,
  textTimeDefaultStyle,
} from './styles';

export function Configuration(): JSX.Element {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [formattedStartHour, setFormattedStartHour] = useState('');
  const [formattedEndHour, setFormattedEndHour] = useState('');

  const { openSnackbar, setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const getDataCallback = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMaintenance();
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      if (response.data) {
        setIsActive(true);
        setStartDate(dayjs(response.data.start_date));
        setEndDate(dayjs(response.data.end_date));
        setFormattedStartHour(response.data.start_time);
        setFormattedEndHour(response.data.end_time);
        setStartTime(
          dayjs(`${response.data.start_date}T${response.data.start_time}`)
        );
        setEndTime(
          dayjs(`${response.data.end_date}T${response.data.end_time}`)
        );
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  const handleStartTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      const endTime = dayjs(newValue).add(1, 'hours');
      setStartTime(newValue);
      setEndTime(endTime);
      setFormattedStartHour(formatTime(newValue));
      setFormattedEndHour(formatTime(endTime));
    }
  };

  const handleEndTime = (newValue: Dayjs | null): void => {
    if (newValue) {
      setEndTime(newValue);
      setFormattedEndHour(formatTime(newValue));
    }
  };

  const handleMaintenance = async (): Promise<void> => {
    if (!startDate || !endDate || !formattedStartHour || !formattedEndHour) {
      setSnackbarMessage('Os campos de data e hora são obrigatórios');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    setDisableButton(true);

    const data: CreateMaintenanceData = {
      end_date: dayjs(endDate).format('YYYY-MM-DD'),
      end_time: formattedEndHour,
      is_active: true,
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      start_time: formattedStartHour,
    };

    try {
      const response = await createMaintenance(data);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Manutenção ativada com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      setIsActive(true);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setDisableButton(false);
    }
  };

  const handleDeleteMaintenance = async (): Promise<void> => {
    setDisableButton(true);
    try {
      const response = await deleteMaintenance();

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setSnackbarMessage('Manutenção cancelada!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      setIsActive(false);
      setStartDate(null);
      setEndDate(null);
      setFormattedStartHour('');
      setFormattedEndHour('');
      setStartTime(null);
      setEndTime(null);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setDisableButton(false);
    }
  };

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>
          {IconSettingsMS} {Constants.configuration}
        </TitleTypography>
      </TitleBox>
      <Box>
        <Typography fontSize="18px" fontWeight={600} mb={2}>
          {Constants.maintenance}
        </Typography>
        {loading ? (
          <LinearProgress sx={{ margin: '40px' }} />
        ) : (
          <>
            {isActive && (
              <Typography
                fontSize="18px"
                fontWeight={600}
                mb={2}
                color="primary"
              >
                {Constants.isActive}
              </Typography>
            )}
            <Typography mt={2}>{Constants.beggining}</Typography>
            <Box display="flex" flexDirection="row" gap={2} my={2}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DesktopDatePicker
                  label="data"
                  value={startDate}
                  format="D [de] MMMM [de] YYYY"
                  disablePast
                  slotProps={{
                    textField: {
                      sx: datePickerStyle,
                    },
                  }}
                  onChange={(e) => setStartDate(e)}
                  disabled={isActive}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopTimePicker
                  label="horário"
                  value={startTime}
                  ampm={false}
                  slotProps={{
                    textField: {
                      sx: textTimeDefaultStyle,
                    },
                  }}
                  onChange={handleStartTime}
                  disabled={isActive}
                />
              </LocalizationProvider>
            </Box>
            <Typography>{Constants.ending}</Typography>
            <Box display="flex" flexDirection="row" gap={2} my={2}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DesktopDatePicker
                  label="data"
                  value={endDate}
                  format="D [de] MMMM [de] YYYY"
                  disablePast
                  slotProps={{
                    textField: {
                      sx: datePickerStyle,
                    },
                  }}
                  onChange={(e) => setEndDate(e)}
                  disabled={isActive}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopTimePicker
                  label="horário"
                  value={endTime}
                  ampm={false}
                  slotProps={{
                    textField: {
                      sx: textTimeDefaultStyle,
                    },
                  }}
                  onChange={handleEndTime}
                  disabled={isActive}
                />
              </LocalizationProvider>
            </Box>
            {isActive ? (
              <Button
                sx={{ my: '24px' }}
                onClick={handleDeleteMaintenance}
                disabled={disableButton}
              >
                {disableButton ? (
                  <WhiteCircularProgress size={22} />
                ) : (
                  Constants.cancel
                )}
              </Button>
            ) : (
              <Button
                sx={{ my: '24px' }}
                onClick={handleMaintenance}
                disabled={disableButton}
              >
                {disableButton ? (
                  <WhiteCircularProgress size={22} />
                ) : (
                  Constants.save
                )}
              </Button>
            )}
          </>
        )}
      </Box>
      <Divider sx={{ my: '16px' }} />
      <Box>
        <Typography fontSize="18px" fontWeight={600} my={2}>
          {Constants.password}
        </Typography>
        <OutlinedButton onClick={() => setOpenDialog(true)}>
          {Constants.changePassword}
        </OutlinedButton>
      </Box>
      {openSnackbar && <Snackbar />}
      <ResetPassword open={openDialog} setOpen={setOpenDialog} />
    </GridMain>
  );
}
